import { createRngState, shuffle, nextRandom } from '../rng/rng';
import { cardDefinitions } from '../cards/cardDefinitions.generated';
import {
  STARTING_DECK_DEFINITION_IDS, HAND_SIZE, PHASES, HIDDEN_SETS,
} from '../constants';
import { buildVillage, buildRandomizedVillage } from '../village/villageSetup';
import {
  selectMonsterGroups, buildDungeonDeck, selectMonsterGroupsByValue, buildFlexibleDungeonDeck,
} from '../dungeon/dungeonDeckSetup';
import { createEmptyHall, compactAndRefill } from '../dungeon/hall';
import { createEmptyTurnFlags } from './turnFlags';
import { createEmptyStats } from './stats';
import { createInitialXpTokenSupply, createEmptyXpTokens } from './xpTokens';
// Side-effecting: registers every card's effect handler. Imported here (not
// just from engine/index.js) so tests that import createGameState directly
// still get a populated effectRegistry - see effectRegistry/registerAll.js.
import '../cards/effectRegistry/registerAll';

// Exported so the UI (Landing.vue's bearerCount input) can cap the house
// rule at however many distinct Bearer cards actually exist, rather than
// letting a player request more than createGameState could ever deliver
// (bearerDefinitionIds.slice(0, bearerCount) - see below - would just
// silently return fewer than asked for). Matt reported this directly: "The
// number of thunderstone bearers needs to be limited to however many
// exist."
export const BEARER_DEFINITION_IDS = cardDefinitions
  .filter((d) => d.type === 'bearer' && !HIDDEN_SETS.has(d.set))
  .map((d) => d.id);

// Builds a complete, ready-to-play GameState: selects the game's hero groups,
// monster groups, village stacks and Thunderstone Bearer; instantiates every
// physical card actually used this game (not the full box); shuffles and deals
// starting hands; and fills the initial dungeon hall.
//
// playerConfigs: [{ id, name, isAI, difficulty }]. Every setup step that consumes
// randomness threads rngState through explicitly rather than mutating a shared
// generator, so the whole function is a pure fn of (playerConfigs, boardSide,
// seed) - the same seed always produces the exact same game.
//
// makeInstance/nextInstanceId ARE mutated locally during this one setup call
// (rather than threaded through every helper's return value) purely to keep
// scripts/buildCardData.mjs-adjacent setup code readable; the function as a
// whole is still referentially transparent from the caller's point of view.
export function createGameState({
  playerConfigs, boardSide, seed, showLiveScores = false, revealDeckContents = false,
  // Matt's house rules - see villageSetup.js's buildRandomizedVillage,
  // lightDarkness.js's computeDarknessPenalty, dungeonDeckSetup.js's
  // selectMonsterGroupsByValue/buildFlexibleDungeonDeck, and resolveCombat.js's
  // own bearerNeverEscapes check for what each actually does.
  randomizedVillage = false, synergyPreferredVillage = false, hardDarkness = false, dungeonDeckTargetValue = null,
  bearerCount = 1, bearerNeverEscapes = false,
  // Matt: "the auto[-end-turn] ability should be optional... make it a
  // check box in the house rules." Read by PlayerDock.vue's own
  // maybeAutoEndTurn - on by default so existing behavior doesn't change
  // for anyone who doesn't touch this setting.
  autoEndTurn = true,
  // Matt asked for this directly, twice: with no table-side negotiation
  // happening in an async digital game, always handing the room creator
  // (seatIndex 0 / playerConfigs[0]) the first turn isn't a rule the
  // rulebook mandates (p.9: "Choose a starting player by whatever method
  // you prefer" - explicitly left open), just a default that fell out of
  // activePlayerIndex always starting at 0. store/index.js's
  // startLocalGame/startRemoteGame both opt into this for real games;
  // defaults to false so every other caller (tests, the tutorial's own
  // fixed-seed script, which depends on 'You' acting first) keeps today's
  // exact behavior unless it explicitly asks for the randomized draw.
  randomizeStartingPlayer = false,
}) {
  let rngState = createRngState(seed);
  let nextInstanceId = 1;
  const instances = {};
  const makeInstance = (definitionId) => {
    const id = `c${nextInstanceId}`;
    nextInstanceId += 1;
    instances[id] = { definitionId };
    return id;
  };

  const [village, afterVillage] = randomizedVillage
    ? buildRandomizedVillage({
      playerCount: playerConfigs.length, makeInstance, rngState, synergyPreferred: synergyPreferredVillage,
    })
    : buildVillage({ playerCount: playerConfigs.length, makeInstance, rngState });
  rngState = afterVillage;

  // Matt: "I always build a deck based on the total value of all the
  // monster levels." Keeps the exact same rngState consumption POSITION as
  // the original (group selection right after the village, before the
  // bearer shuffle) for the plain default case (dungeonDeckTargetValue
  // unset) - only the selection ALGORITHM changes when this house rule is
  // on, never the order other setup steps consume randomness in.
  const [selectedMonsterGroups, afterGroups] = dungeonDeckTargetValue != null
    ? selectMonsterGroupsByValue(rngState, dungeonDeckTargetValue)
    : selectMonsterGroups(rngState);
  rngState = afterGroups;

  // Matt: "I usually include multiple thunderstone bearers in my deck."
  // Always shuffles the full bearer pool once (even for the standard
  // bearerCount === 1 case) so the RNG stream stays identical to before
  // this house rule existed - only the SLICE size changes.
  const [bearerShuffled, afterBearerShuffle] = shuffle(BEARER_DEFINITION_IDS, rngState);
  rngState = afterBearerShuffle;
  const bearerDefinitionIds = bearerShuffled.slice(0, Math.max(1, bearerCount));

  // Either house rule (variable-value deck sizing, or more than 1 bearer)
  // routes through the flexible deck-builder, which doesn't assume a fixed
  // 30-card top-20/bottom-10 split; the plain default path (bearerCount 1,
  // no target value) keeps today's exact buildDungeonDeck behavior/RNG
  // stream unchanged.
  const usesFlexibleDeck = dungeonDeckTargetValue != null || bearerCount > 1;
  const [dungeonDeck, afterDeck] = usesFlexibleDeck
    ? buildFlexibleDungeonDeck({
      selectedGroups: selectedMonsterGroups, bearerDefinitionIds, makeInstance, rngState,
    })
    : buildDungeonDeck({
      selectedGroups: selectedMonsterGroups, bearerDefinitionId: bearerDefinitionIds[0], makeInstance, rngState,
    });
  rngState = afterDeck;

  const players = [];
  for (const config of playerConfigs) {
    const deckInstanceIds = [];
    for (const [defId, count] of Object.entries(STARTING_DECK_DEFINITION_IDS)) {
      for (let i = 0; i < count; i += 1) deckInstanceIds.push(makeInstance(defId));
    }
    const [shuffledDeck, afterPlayerShuffle] = shuffle(deckInstanceIds, rngState);
    rngState = afterPlayerShuffle;
    const hand = shuffledDeck.slice(0, HAND_SIZE);
    const deck = shuffledDeck.slice(HAND_SIZE);
    players.push({
      id: config.id,
      name: config.name,
      isAI: Boolean(config.isAI),
      // 'easy' | 'normal' | 'hard' | 'expert' - looked up against
      // villageHeuristics.js's DIFFICULTY_PRESETS at store/index.js's
      // playAITurn call sites. Meaningless for a non-AI player.
      difficulty: config.difficulty || 'normal',
      deck,
      hand,
      discard: [],
      handRevealed: false,
      xp: 0,
      // Into the Abyss, Heart of Doom (Thunderstone Bearer): "Aftermath: If
      // this card is not defeated, the active player loses." Matt,
      // confirming the mechanic: true elimination - the losing player is
      // removed from turn rotation entirely and from winner contention,
      // but the game continues normally for everyone else UNLESS this
      // leaves exactly one player remaining, in which case the game ends
      // immediately and that player wins outright. See
      // cards/effectHelpers.js's eliminatePlayer, actions/turnActions.js's
      // endTurn (skips eliminated players when picking the next active
      // player), and scoring/scoring.js's computeFinalScores (excludes
      // them from winner contention).
      eliminated: false,
      // Numenera's colored-XP rule (see state/xpTokens.js) - kept in sync
      // with the plain `xp` number above via cards/effectHelpers.js's
      // grantXp, which is the only place either field is ever written.
      xpTokens: createEmptyXpTokens(),
      // familiarInstanceId: set once, permanently, the first time a player
      // takes a familiar (rulebook, "Familiars": "a familiar is yours for
      // the rest of the game") - this identity never changes again, unlike
      // whether it's currently "out" (see inPlayInstanceIds below).
      familiarInstanceId: null,
      // heroInstanceId -> weaponInstanceId[]. Almost every hero can hold at
      // most one (Caverns of Bane's Disowned Bloodrager is the one exception,
      // capacity 2 - see effectHelpers.js's getWeaponCapacity); a given
      // weapon instance can only be in one hero's list at a time (enforced by
      // dungeonActions.equipWeapon, not by this shape alone).
      equippedWeapons: {},
      // The generic "in front of you" zone - anything currently sitting
      // outside hand/deck/discard, owned by (or, for Mind Control, merely
      // assigned to) this player:
      //  - the familiar, whenever it's currently "out" (familiarInstanceId
      //    identifies WHICH card; membership here says whether it's
      //    currently in play vs cycling through hand/deck/discard like a
      //    normal card between uses - see turnActions.endTurn).
      //  - Root of Corruption, Circle of Protection: "On your turn, place
      //    this card in front of you. It remains in play." See
      //    effectHelpers.js's placeInFrontOfPlayer/consumeCircleOfProtection.
      //  - Root of Corruption, Mind Control: "Place 1 monster from the hall
      //    in front of a player... that player must enter the dungeon and
      //    attack that monster if able." A monster instance is
      //    distinguishable from everything else here by its own `type`
      //    (state/selectors.js's getAssignedMonsterInstanceId) - it isn't
      //    owned by this player the way the other two are (excluded from
      //    getPlayerCollectionInstanceIds), and its own presence alone
      //    creates a forced-fight obligation (hasAssignedMonsterObligation)
      //    that the other two kinds never do.
      // A single array, not one field per use, precisely so a future card
      // needing this same "sits outside hand, persists" shape doesn't need
      // a 4th parallel mechanism - Matt asked for this directly: "I think
      // the 'in front of you' zone is a concept we could use for the
      // familiar as well... maybe even use this for cards that get put in
      // front of other players by monster effects or other things."
      inPlayInstanceIds: [],
      // Into the Abyss, Grim (all 4 levels): "Spoils: Set the defeated
      // monster [and this card / and any monsters present] aside. Return
      // [it/them] to your deck at the end of the game." A permanent,
      // untouchable-until-the-game-is-over zone - deliberately NOT
      // inPlayInstanceIds above (that array excludes monster-type cards
      // from scoring for Mind Control's own unrelated reason - an assigned
      // monster there is merely ASSIGNED, not owned/scored - whereas a
      // set-aside monster here IS owned and DOES score). See
      // cards/effectHelpers.js's setAsideForGame and state/selectors.js's
      // getPlayerCollectionInstanceIds (which folds this zone into scoring
      // directly - functionally identical to the printed "return to your
      // deck at the end of the game," since nothing distinguishes deck vs.
      // any other owned zone once the game is already over and no more
      // turns will read it).
      setAsideInstanceIds: [],
      turnFlags: createEmptyTurnFlags(),
      stats: createEmptyStats(),
    });
  }

  // Drawn from the same seeded RNG every other setup step already threads
  // through rngState, rather than Math.random() (banned in src/engine/** by
  // lint anyway) - keeps replays/seeds fully deterministic, same as the
  // shuffles above. Only actually rolled when asked for (see
  // randomizeStartingPlayer's own comment above) - still consumes an rngState
  // step even when unused would be harmless, but skipping it entirely keeps
  // every pre-existing seed's downstream draws (none currently depend on
  // this one anyway, since it's the last draw of setup) untouched.
  let activePlayerIndex = 0;
  if (randomizeStartingPlayer) {
    const [startingPlayerRoll, afterStartingPlayerRoll] = nextRandom(rngState);
    rngState = afterStartingPlayerRoll;
    activePlayerIndex = Math.floor(startingPlayerRoll * players.length);
  }

  let state = {
    meta: {
      seed,
      rngState,
      boardSide,
      phase: PHASES.AWAITING_ACTION,
      activePlayerIndex,
      turnNumber: 1,
      gameOver: false,
      winnerId: null,
      endTrigger: null,
      // Caverns of Bane, Troll Skullcracker: "Raid: The next player must
      // enter the dungeon and fight this card." See
      // effectRegistry/monsters.js's own registration - set when its Raid
      // resolves, cleared once the obligated player actually fights it (or
      // silently treated as void if it's no longer in the hall by then).
      forcedFightObligation: null,
      // Matt: "It would be cool to be able to see the current point count
      // for yourself and your opponents, but it should default to not
      // showing that... a way to turn that on for the game you're playing
      // as a setting when you first start the game." Chosen once, at
      // game-start time (Landing.vue/CreateGame.vue), not adjustable
      // mid-game.
      showLiveScores,
      // Matt: "I don't think this is really a memorization game so I don't
      // mind knowing what's in the deck, as long as I don't know what order
      // they are in. Maybe we can make this something you could turn on as
      // an optional flag when you set the game up." Same "chosen once, at
      // game-start time, not adjustable mid-game" convention as
      // showLiveScores above. Only gates what "My Deck" shows
      // (PlayerDock.vue/CollectionPanel.vue) - order itself is never
      // revealed either way, since that view only ever shows grouped/
      // counted cards, never sequence.
      revealDeckContents,
      // Matt's house rules, all chosen once at game-start time (same
      // "not adjustable mid-game" convention as the two settings above) -
      // see each one's own consuming code for what it actually does:
      // villageSetup.js's buildRandomizedVillage, lightDarkness.js's
      // computeDarknessPenalty, dungeonDeckSetup.js's
      // selectMonsterGroupsByValue/buildFlexibleDungeonDeck (both above,
      // this file), and resolveCombat.js's own bearerNeverEscapes check.
      randomizedVillage,
      synergyPreferredVillage,
      hardDarkness,
      dungeonDeckTargetValue,
      bearerCount,
      bearerNeverEscapes,
      autoEndTurn,
    },
    players,
    instances,
    dungeonDeck,
    dungeonHall: createEmptyHall(boardSide),
    // xpTokenSupply: the shared 9-per-color bank Numenera's colored-XP rule
    // draws from (see state/xpTokens.js) - lives on `village` alongside the
    // other shared pools (curseDeck, basicStacks) it's conceptually grouped with.
    village: { ...village, xpTokenSupply: createInitialXpTokenSupply() },
    log: [],
  };

  state = compactAndRefill(state, { isInitialSetup: true }); // initial hall fill from the freshly-built deck

  return state;
}
